import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useTerminology } from '@/lib/useTerminology'
import { fetchOpenPositions, takeOpenPositions } from '../api/incoming-invoices.api'
import { abgleichStand, imAbgleich } from '../lib/abgleich'
import { ABGLEICH_TEXT, mengeText, tagText } from '../lib/texte'
import type { IncomingInvoiceDetail, OpenPosition } from '../types/incoming-invoices.types'

const knopf = 'rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50'

/**
 * AP-34 9. — **Positionen aus der Bestellung anlegen**: offene Bestellpositionen des Lieferanten
 * ankreuzen und als Rechnungspositionen anlegen. Zu sehen ist zuerst nur der Knopf (Auftraggeber
 * 4.10.2026); die Liste wird erst geladen und gezeigt, wenn er gedrückt wird. Angelegt wird die offene
 * Menge zum vereinbarten Preis — dem der bestätigten Auftragsbestätigung, wenn es eine gibt
 * (Nachtrag 1, 6.2).
 *
 * **Zugeordnet wird hier nichts** (Nachtrag 3, PT-9/PT-10): Wer eine gelesene Position einer
 * Bestellposition zuordnen will, öffnet den „Abgleich". Der Knopf hieß „Aus Bestellung übernehmen"
 * und wurde für die Zuordnung gehalten — dann stand die Position doppelt in der Rechnung.
 *
 * **Angeboten wird nur, was sich noch anlegen lässt** (Nachtrag 4, PT-16): Eine Bestellposition, die in
 * dieser Rechnung schon steht — angelegt oder im Abgleich zugeordnet —, verschwindet aus der Liste, ebenso
 * eine, die andere Rechnungen ganz verrechnet haben. Wird die Position aus der Rechnung entfernt oder ihr
 * Bezug gelöst, ist sie wieder da. So kommt keine bestellte Position zweimal in eine Rechnung.
 */
export function Bestellpositionen({
  rechnung,
  onRechnung,
  onFehler,
  onAbgleich,
}: {
  rechnung: IncomingInvoiceDetail
  onRechnung: (r: IncomingInvoiceDetail) => void
  onFehler: (t: string | null) => void
  /** öffnet die Ansicht „Abgleich" (Nachtrag 3, E-7) */
  onAbgleich: () => void
}) {
  const terms = useTerminology()
  const [positionen, setPositionen] = useState<OpenPosition[] | null>(null)
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set())
  const [offen, setOffen] = useState(false)
  const [laeuft, setLaeuft] = useState(false)

  // Neu laden, sobald sich an Lieferant, Art oder den Bezügen und Mengen der Zeilen etwas ändert
  const stand = useMemo(() => [rechnung.supplierId, rechnung.kind, ...rechnung.lines.map((z) => `${z.id}:${z.sourceLineId ?? ''}:${z.quantity}`)].join('|'), [rechnung.supplierId, rechnung.kind, rechnung.lines])
  useEffect(() => {
    if (!offen) return
    let aus = false
    fetchOpenPositions(rechnung.id)
      .then((p) => !aus && setPositionen(p))
      .catch(() => !aus && setPositionen([]))
    return () => {
      aus = true
    }
  }, [rechnung.id, stand, offen])

  const tue = async (was: () => Promise<IncomingInvoiceDetail>, fehlertext: string) => {
    try {
      setLaeuft(true)
      onFehler(null)
      onRechnung(await was())
    } catch (e) {
      onFehler(apiErrorText(e, fehlertext))
    } finally {
      setLaeuft(false)
    }
  }

  const gutschrift = rechnung.kind === 'CREDIT_NOTE'
  // Übernehmbar ist, was offen ist — bei einer Gutschrift, was verrechnet ist
  const menge = (p: OpenPosition) => (gutschrift ? p.offen - Number(p.gelieferteMenge ?? p.bestellteMenge) : p.offen)
  const waehlbar = (p: OpenPosition) => rechnung.editable && (gutschrift ? menge(p) < 0 : menge(p) > 0)
  // Was im Abgleich noch zu tun ist — gezählt erst, wenn die Rechnung überhaupt einen Bezug hat
  const imAbgleichOffen = rechnung.matchStatus === 'NONE' ? [] : rechnung.lines.filter(imAbgleich)
  const zuKlaeren = imAbgleichOffen.filter((z) => abgleichStand(z) === 'OFFEN').length
  const zuBestaetigen = rechnung.lines.filter((z) => imAbgleich(z) && abgleichStand(z) === 'PRUEFEN').length
  const status = ABGLEICH_TEXT[rechnung.matchStatus] ?? ABGLEICH_TEXT.NONE

  // PT-16: Was schon in dieser Rechnung steht, wird nicht noch einmal angeboten — auch nicht mit seiner Restmenge.
  const liste = useMemo(
    () => (positionen ?? []).filter((p) => p.inDieserRechnung === 0 && (gutschrift ? p.offen - Number(p.gelieferteMenge ?? p.bestellteMenge) < 0 : p.offen > 0)),
    [positionen, gutschrift],
  )
  const schonHier = (positionen ?? []).filter((p) => p.inDieserRechnung !== 0).length
  const bestellungen = useMemo(() => {
    const aus = new Map<string, { nummer: string; datum: string | null; projekt: string | null; zeilen: OpenPosition[] }>()
    for (const p of liste) {
      const b = aus.get(p.bestellungId) ?? { nummer: p.bestellnummer, datum: p.bestelldatum, projekt: [p.projectNumber, p.projectName].filter(Boolean).join(' ') || null, zeilen: [] }
      b.zeilen.push(p)
      aus.set(p.bestellungId, b)
    }
    return [...aus.entries()]
  }, [liste])

  // Angekreuzt zählt nur, was noch in der Liste steht — eine Position kann inzwischen in die Rechnung gekommen sein.
  const auswahl = liste.filter((p) => gewaehlt.has(p.quelleId)).map((p) => p.quelleId)
  const uebernehmen = async () => {
    const ids = auswahl
    await tue(() => takeOpenPositions(rechnung.id, ids), 'Die Positionen ließen sich nicht anlegen.')
    setGewaehlt(new Set())
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={`inline-flex items-center gap-2 ${knopf} ${zuKlaeren + zuBestaetigen > 0 ? 'border-primary/60 font-medium' : ''}`}
          // Ein `title` allein würde den Namen des Knopfs für Vorlesehilfen ersetzen.
          aria-label={['Abgleich', zuKlaeren > 0 && `${zuKlaeren} zu klären`, zuBestaetigen > 0 && `${zuBestaetigen} zu bestätigen`].filter(Boolean).join(', ')}
          title="Stellt jede Rechnungsposition neben ihre Bestellposition — zuordnen, bestätigen, ohne Bezug lassen"
          onClick={onAbgleich}
        >
          Abgleich
          {zuKlaeren > 0 && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">{zuKlaeren} zu klären</span>}
          {zuBestaetigen > 0 && <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-medium text-sky-700 dark:text-sky-300">{zuBestaetigen} zu bestätigen</span>}
        </button>
        {rechnung.matchStatus !== 'NONE' && <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${status.klasse}`}>{status.label}</span>}
        <span className="ml-auto flex flex-wrap gap-2">
          {rechnung.editable && (
            <button
              type="button"
              className={`inline-flex items-center gap-1 ${knopf}`}
              onClick={() => setOffen((v) => !v)}
              aria-expanded={offen}
              aria-label="Positionen aus Bestellung anlegen"
              title="Legt neue Rechnungspositionen aus offenen Bestellpositionen an — für Positionen, die noch nicht in der Rechnung stehen"
            >
              {offen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              {gutschrift ? 'Positionen aus Bestellung anlegen (verrechnet)' : 'Positionen aus Bestellung anlegen'}
              {offen && positionen ? ` (${liste.length})` : ''}
            </button>
          )}
          {offen && rechnung.editable && (
            <button type="button" className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50" disabled={laeuft || auswahl.length === 0} onClick={() => void uebernehmen()}>
              Anlegen{auswahl.length ? ` (${auswahl.length})` : ''}
            </button>
          )}
        </span>
      </div>

      {offen && positionen !== null && liste.length > 0 && rechnung.lines.some((z) => imAbgleich(z) && !z.sourceLineId) && (
        <p className="text-xs text-muted-foreground">
          Hier entstehen <strong>neue</strong> Positionen. Steht die Position schon in der Rechnung, ordne sie im Abgleich zu — sonst steht sie doppelt da.
        </p>
      )}

      {offen && positionen === null && <p className="text-sm text-muted-foreground">Laden…</p>}

      {offen && positionen !== null && liste.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {schonHier > 0
            ? 'Alle offenen Bestellpositionen dieses Lieferanten stehen schon in dieser Rechnung.'
            : gutschrift
              ? 'Zu diesem Lieferanten ist nichts verrechnet.'
              : 'Zu diesem Lieferanten gibt es keine offene Bestellposition.'}
        </p>
      )}

      {offen && positionen !== null && liste.length > 0 && schonHier > 0 && (
        <p className="text-xs text-muted-foreground" role="status">
          {schonHier === 1 ? 'Eine Bestellposition steht' : `${schonHier} Bestellpositionen stehen`} schon in dieser Rechnung und {schonHier === 1 ? 'wird' : 'werden'} hier nicht mehr angeboten — zu sehen im Abgleich.
        </p>
      )}

      {offen &&
        bestellungen.map(([id, b]) => (
          <div key={id} className="overflow-x-auto rounded-md border border-border">
            <div className="flex flex-wrap items-baseline gap-x-3 bg-muted/50 px-3 py-1.5 text-sm">
              <span className="font-medium">Bestellung {b.nummer}</span>
              {b.datum && <span className="text-muted-foreground">vom {tagText(b.datum)}</span>}
              {b.projekt && (
                <span className="text-muted-foreground">
                  {terms.project} {b.projekt}
                </span>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="w-8 px-3 py-1" />
                  <th className="px-2 py-1 font-normal">Pos.</th>
                  <th className="px-2 py-1 font-normal">Bezeichnung</th>
                  <th className="px-2 py-1 text-right font-normal">bestellt</th>
                  <th className="px-2 py-1 text-right font-normal">offen</th>
                  <th className="px-2 py-1 font-normal">Einheit</th>
                  <th className="px-3 py-1 text-right font-normal">Preis</th>
                </tr>
              </thead>
              <tbody>
                {b.zeilen.map((p) => {
                  const bestaetigt = p.bestaetigterEinzelpreis !== null
                  const preis = Number(bestaetigt ? p.bestaetigterEinzelpreis : p.einzelpreis)
                  const pe = Number(bestaetigt ? p.bestaetigtePreiseinheit : p.preiseinheit) || 1
                  const rabatt = Number(bestaetigt ? p.bestaetigterRabattProzent : p.rabattProzent)
                  const abweichend = bestaetigt && Number(p.bestaetigterEinzelpreis) !== Number(p.einzelpreis)
                  return (
                    <tr key={p.quelleId} className="border-t border-border">
                      <td className="px-3 py-1">
                        <input
                          type="checkbox"
                          aria-label={`Position ${p.bestellzeileNr} anlegen`}
                          disabled={!waehlbar(p)}
                          checked={gewaehlt.has(p.quelleId)}
                          onChange={(e) => {
                            const neu = new Set(gewaehlt)
                            if (e.target.checked) neu.add(p.quelleId)
                            else neu.delete(p.quelleId)
                            setGewaehlt(neu)
                          }}
                        />
                      </td>
                      <td className="px-2 py-1 tabular-nums text-muted-foreground">{p.bestellzeileNr}</td>
                      <td className="px-2 py-1">
                        {p.bezeichnung}
                        {(p.articleNumber || p.supplierArticleNumber) && <span className="ml-2 text-xs text-muted-foreground">{[p.articleNumber, p.supplierArticleNumber].filter(Boolean).join(' · ')}</span>}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">{mengeText(Number(p.bestellteMenge))}</td>
                      <td className={`px-2 py-1 text-right tabular-nums ${p.offen <= 0 ? 'text-muted-foreground' : 'font-medium'}`}>{mengeText(p.offen)}</td>
                      <td className="px-2 py-1">{p.unitCode}</td>
                      <td className="px-3 py-1 text-right tabular-nums">
                        {abweichend && <span className="mr-2 text-xs text-muted-foreground line-through">{geldText(Number(p.einzelpreis))}</span>}
                        {geldText(preis)}
                        {pe !== 1 && <span className="text-xs text-muted-foreground"> / {pe}</span>}
                        {rabatt > 0 && <span className="text-xs text-muted-foreground"> −{mengeText(rabatt)} %</span>}
                        {bestaetigt && (
                          <span className="ml-1 rounded bg-emerald-500/15 px-1 text-[11px] text-emerald-700 dark:text-emerald-300" title={p.bestaetigtAm ? `Auftragsbestätigung bestätigt am ${tagText(p.bestaetigtAm)}` : 'laut Auftragsbestätigung'}>
                            bestätigt
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  )
}
